import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, Loader2, RotateCcw, ShieldCheck } from "lucide-react";

const MODES = [
  { v: "replace", l: "Tam olarak o ana dönsün", d: "Veriler güvenlik yedeğindeki haliyle tamamen değiştirilir." },
  { v: "merge", l: "Yalnızca eksik kayıtlar geri gelsin", d: "Mevcut kayıtlar korunur; silinmiş olanlar geri eklenir." },
];

export const SafetyBackupCard = ({ refreshKey, onDone }) => {
  const [meta, setMeta] = useState(null);
  const [mode, setMode] = useState("replace");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/backup/safety").then((r) => setMeta(r.data)).catch(() => setMeta({}));
  }, [refreshKey]);

  const download = async () => {
    setBusy("download");
    try {
      await downloadFile("/backup/safety/download", "khgb_guvenlik_yedegi.json");
      toast.success("Güvenlik yedeği indirildi");
    } catch (e) { toast.error("Güvenlik yedeği indirilemedi"); }
    finally { setBusy(""); }
  };

  const revert = async () => {
    setConfirm(false);
    setBusy("revert");
    try {
      const r = await api.post("/backup/safety/revert", { mode });
      setMeta(r.data.safety);
      toast.success(`Güvenlik yedeğine dönüldü (${r.data.tables.reduce((s, t) => s + t.restored, 0)} kayıt)`);
      onDone?.(r.data.tables);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Güvenlik yedeğine dönülemedi");
    } finally { setBusy(""); }
  };

  const total = meta?.counts ? Object.values(meta.counts).reduce((s, n) => s + n, 0) : 0;

  return (
    <Card className="p-6 border border-slate-200 shadow-sm max-w-3xl mt-6" data-testid="safety-backup-card">
      <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-700" /> Sunucudaki Güvenlik Yedeği</h3>
      <p className="text-sm text-slate-500 mt-1">
        Her geri yüklemeden hemen önce verilerin o anki hali sunucuda saklanır. Yalnızca son güvenlik yedeği tutulur.
      </p>
      {!meta ? <div className="text-sm text-slate-500 mt-4">Yükleniyor...</div> : !meta.created_at ? (
        <div className="text-sm text-slate-500 mt-4" data-testid="safety-backup-empty">Henüz güvenlik yedeği yok. İlk geri yüklemede otomatik oluşturulur.</div>
      ) : (
        <>
          <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 px-4 py-3 text-sm" data-testid="safety-backup-info">
            <div className="font-medium text-slate-900">{new Date(meta.created_at).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" })}</div>
            <div className="text-slate-600">{meta.reason} · {meta.created_by} · {total} kayıt</div>
          </div>
          <RadioGroup value={mode} onValueChange={setMode} className="mt-4 gap-3" data-testid="safety-mode-group">
            {MODES.map((m) => (
              <label key={m.v} className={`flex gap-3 rounded-md border p-3 cursor-pointer ${mode === m.v ? "border-emerald-600 bg-emerald-50/40" : "border-slate-200"}`}>
                <RadioGroupItem value={m.v} className="mt-0.5" data-testid={`safety-mode-${m.v}`} />
                <div><div className="text-sm font-medium text-slate-900">{m.l}</div><div className="text-xs text-slate-500">{m.d}</div></div>
              </label>
            ))}
          </RadioGroup>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={() => setConfirm(true)} disabled={!!busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="safety-revert-button">
              {busy === "revert" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />} Bu Yedeğe Dön
            </Button>
            <Button variant="outline" onClick={download} disabled={!!busy} data-testid="safety-download-button">
              {busy === "download" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Bilgisayara İndir
            </Button>
          </div>
        </>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="safety-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Güvenlik yedeğine dönülsün mü?</AlertDialogTitle>
            <AlertDialogDescription>
              {mode === "replace" ? "Veriler güvenlik yedeğindeki haliyle tamamen değiştirilecek." : "Güvenlik yedeğinde olup şu an olmayan kayıtlar geri eklenecek."}
              {" "}Dönmeden önce şimdiki veriler yeni güvenlik yedeği olarak saklanacak; böylece bu işlemi de geri alabilirsiniz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="safety-cancel-button">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={revert} className={mode === "replace" ? "bg-rose-700 hover:bg-rose-800" : "bg-emerald-700 hover:bg-emerald-800"}
                               data-testid="safety-confirm-button">Evet, dön</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
